import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Box from '@mui/material/Box';
import MuiCheckbox from '@mui/material/Checkbox';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import { useFormControl } from '@mui/material/FormControl';
import { FormErrorText, FormHelperText } from '@famly/mf_input_form-helper-text';
import { useTheme } from '@famly/mf_theme';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { Text } from '@famly/mf_data-display_text';
import { Stack } from '@famly/mf_layout_stack';
import { extractSystemStyles } from '@famly/mf_system_system-props';
import { useResponsiveSize } from '@famly/mf_util_breakpoints';
import { CheckboxTestAttributes } from './test-attributes';
const getSize = (size) => {
    switch (size) {
        case 'compact':
            return '16px';
        case 'regular':
        default:
            return '24px';
    }
};
const getOutlineSize = (size) => {
    switch (size) {
        case 'compact':
            return '1px';
        case 'regular':
        default:
            return '2px';
    }
};
const color = (color) => (props) => props.theme.modernFamlyTheme.colorPalette[color];
const Unchecked = styled('span', { shouldForwardProp: prop => prop !== 'size' && prop !== 'error' }) `
    border-radius: 4px;
    background-color: ${color('n0')};
    border: 1px solid ${color('n200')};

    width: ${props => getSize(props.size)};
    height: ${props => getSize(props.size)};

    .Mui-focusVisible & {
        outline: ${props => getOutlineSize(props.size)} solid ${color('p100')};
        border: 1px solid ${color('p400')};
    }

    .Mui-disabled & {
        background-color: ${color('n75')};
    }

    /* MUI doesn't seem to expose a class representing the error state for checkboxes */
    ${props => props.error
    ? `
                  outline: ${getOutlineSize(props.size)} solid ${props.theme.modernFamlyTheme.colorPalette.r200};
                  border: 1px solid ${props.theme.modernFamlyTheme.colorPalette.r400};
              `
    : ''}
`;
const Checked = styled(Unchecked) `
    background-color: ${color('p400')};
`;
const Checkmark = ({ fill }) => {
    return (_jsx("svg", { width: "10", height: "8", viewBox: "0 0 10 8", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M1.29683 4.44483C1.06809 4.20506 0.688299 4.19612 0.448535 4.42486C0.20877 4.65359 0.19983 5.03339 0.428565 5.27315L2.81028 7.7697C3.05552 8.02677 3.46919 8.01562 3.70023 7.74571L9.42764 1.05463C9.64312 0.802892 9.61373 0.424132 9.36199 0.208648C9.11025 -0.00683625 8.73149 0.0225553 8.51601 0.274296L3.5081 6.12481C3.35407 6.30475 3.07829 6.31219 2.9148 6.14081L1.29683 4.44483Z", fill: fill }) }));
};
/**
 * Indeterminate mark (minus sign) for partial selection state.
 * Displayed when the checkbox is in an indeterminate state,
 * e.g. when only some items in a group are selected.
 */
const IndeterminateMark = ({ fill }) => {
    return (_jsx("svg", { width: "8", height: "2", viewBox: "0 0 8 2", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("rect", { width: "8", height: "2", rx: "1", fill: fill }) }));
};
export const StandaloneCheckbox = ({ ref, ...props }) => {
    var _a;
    const { id, size: sizeFromProps, error: errorFromProps, disabled: disabledFromProps } = props;
    const size = useResponsiveSize(sizeFromProps);
    const { colorPalette } = useTheme();
    const { error: errorFromFormControl, disabled: disabledFromFormControl } = (_a = useFormControl()) !== null && _a !== void 0 ? _a : {};
    const error = errorFromProps !== null && errorFromProps !== void 0 ? errorFromProps : errorFromFormControl;
    const disabled = disabledFromProps !== null && disabledFromProps !== void 0 ? disabledFromProps : disabledFromFormControl;
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);
    const isIndeterminate = props.checked === 'indeterminate';
    const isChecked = props.checked === true;
    const iconFill = disabled ? colorPalette.n300 : colorPalette.n0;
    return (_jsx(MuiCheckbox, { id: id, disableRipple: true, checked: isChecked, indeterminate: isIndeterminate, icon: _jsx(Unchecked, { size: size, error: error }), onChange: props.onChange, onClick: props.onClick, checkedIcon: _jsx(Checked, { size: size, error: error, children: _jsx(Box, { display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%", children: _jsx(Checkmark, { fill: iconFill }) }) }), indeterminateIcon: _jsx(Checked, { size: size, error: error, children: _jsx(Box, { display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%", children: _jsx(IndeterminateMark, { fill: iconFill }) }) }), disabled: disabled, inputRef: ref, inputProps: props.inputProps, ...dataProps, ...ariaProps }));
};
export const Checkbox = ({ ref, ...props }) => {
    const { id, labelProps, required = false, size: sizeFromProps, checked, children } = props;
    const isChecked = checked === true;
    const size = useResponsiveSize(sizeFromProps);
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);
    const { sx: alignmentSx } = extractSystemStyles(props);
    const checkboxSize = getSize(size);
    return (_jsxs(Box, { sx: alignmentSx, children: [_jsx(MuiFormControlLabel, { htmlFor: labelProps === null || labelProps === void 0 ? void 0 : labelProps.htmlFor, control: _jsx(StandaloneCheckbox, { id: id, size: size, disabled: props.disabled, error: props.error, onChange: props.onChange, checked: props.checked, onClick: props.onClick, ref: ref, ...ariaProps }), label: _jsxs(_Fragment, { children: [_jsxs(Stack, { component: "span", children: [_jsx(Text, { variant: size === 'compact' ? 'body-small' : 'body', component: "span", children: props.label }), required ? (_jsx(Text, { variant: size === 'compact' ? 'body-small' : 'body', component: "span", color: props.disabled ? 'n300' : 'r400', children: "\u2009*" })) : null] }), props.errorMessage ? (_jsx(FormErrorText, { error: true, "data-e2e-class": CheckboxTestAttributes.errorText, children: props.errorMessage })) : null, props.helperText ? _jsx(FormHelperText, { children: props.helperText }) : null] }), sx: theme => ({
                    alignItems: 'flex-start',
                    // By default, MUI sets a marginLeft to 11px,
                    // but that leads to the checkbox element getting "pulled out" of its container.
                    marginLeft: '-9px',
                    // By default, MUI sets a marginRight on the checkbox to 16px, which is not necessary
                    marginRight: '0',
                    '& .MuiFormControlLabel-label': {
                        fontSize: theme.typography[size === 'compact' ? 'body-small' : 'body'].fontSize,
                        lineHeight: theme.typography[size === 'compact' ? 'body-small' : 'body'].lineHeight,
                        color: theme.modernFamlyTheme.colorPalette.n400,
                        // Pushes down the label such that the main label text is horizontally aligned with the checkbox
                        marginTop: size === 'compact' ? '10px' : '12px',
                        // Removes MUI's label color choice when checkbox is disabled.
                        // In our designs, the label has the same color regardless of disabled state.
                        '&.Mui-disabled': {
                            color: theme.modernFamlyTheme.colorPalette.n300,
                        },
                    },
                }), ...dataProps }), children ? (_jsx(Box, { sx: {
                    marginLeft: `calc(${checkboxSize} + 8px)`,
                    marginTop: isChecked ? '8px' : '0',
                    overflow: 'hidden',
                    maxHeight: isChecked ? '1000px' : '0px',
                    opacity: isChecked ? 1 : 0,
                    transition: 'max-height 200ms ease-in-out, opacity 200ms ease-in-out, margin-top 200ms ease-in-out',
                }, tabIndex: isChecked ? undefined : -1, children: children })) : null] }));
};
